import { useMemo } from "react";
import { TempoChatListItemDto } from "./tempo-chat-list.typing";

interface UseTempoChatListFilterBarProps {
  tempoChats: TempoChatListItemDto[] | undefined;
  searchValue: string | null;
  isUnreadOnly: boolean;
  targetLanguageFilterValue?: string;
  chatsFilterValue: string;
  customerFilterValue?: string;
  sortValue: string;
  sortDirection: "asc" | "desc";
  tempoChatUnreadCounts: Record<number, number> | undefined;
}

export const useTempoChatListFilterBar = ({
  tempoChats,
  searchValue,
  isUnreadOnly,
  targetLanguageFilterValue,
  chatsFilterValue,
  customerFilterValue,
  sortValue,
  sortDirection,
  tempoChatUnreadCounts,
}: UseTempoChatListFilterBarProps) => {
  const filteredAndSortedChats = useMemo(() => {
    if (!tempoChats) return [];

    return tempoChats
      .sort((a, b) => {
        let result = 0;

        if (sortValue === "last-message") {
          result = new Date(b.lastMessageTime).getTime() - new Date(a.lastMessageTime).getTime();
        } else if (sortValue === "correspondent-name") {
          result = a.lastMessageAuthorName.localeCompare(b.lastMessageAuthorName);
        } else if (sortValue === "project-name") {
          result = a.projectName.localeCompare(b.projectName);
        } else if (sortValue === "target-language") {
          result = a.targetLanguage.localeCompare(b.targetLanguage);
        } else if (sortValue === "task-code") {
          result = a.taskCode.localeCompare(b.taskCode);
        } else if (sortValue === "customer") {
          result = a.customerName.localeCompare(b.customerName);
        } else {
          // Default fallback to last message time if sortValue doesn't match - shouldn't happen but just in case
          result = new Date(b.lastMessageTime).getTime() - new Date(a.lastMessageTime).getTime();
        }
        return sortDirection === "desc" ? result : -result;
      })
      .filter((chat) => {
        if (searchValue && searchValue.trim() !== "") {
          const searchLower = searchValue.toLowerCase().trim();
          const matchesSearch =
            chat.taskCode.toLowerCase().includes(searchLower) || chat.projectName.toLowerCase().includes(searchLower);
          if (!matchesSearch) return false;
        }
        return true;
      })
      .filter((chat) => {
        if (!isUnreadOnly) return true;
        return (tempoChatUnreadCounts?.[chat.id] ?? 0) > 0;
      })
      .filter((chat) => {
        if (!targetLanguageFilterValue || targetLanguageFilterValue === "all-languages") return true;
        return chat.targetLanguage === targetLanguageFilterValue;
      })
      .filter((chat) => {
        if (chatsFilterValue === "all-chats") {
          return true;
        }
        if (chatsFilterValue === "active-chats") {
          return !chat.closed;
        }
        if (chatsFilterValue === "closed-chats") {
          return chat.closed;
        }
      })
      .filter((chat) => {
        if (!customerFilterValue || customerFilterValue === "all-customers") return true;
        return chat.customerName === customerFilterValue;
      });
  }, [
    tempoChats,
    searchValue,
    isUnreadOnly,
    tempoChatUnreadCounts,
    targetLanguageFilterValue,
    chatsFilterValue,
    customerFilterValue,
    sortValue,
    sortDirection,
  ]);

  return {
    filteredAndSortedChats,
  };
};
